import * as React from "react";
import {
  Text,
  Heading,
  Stack,
  Box,
  Table,
  Tbody,
  Tr,
  Td,
  UnorderedList,
} from "@chakra-ui/react";

import MetaHead from "components/MetaHead";
import Container from "components/Chakra/Container";
import TERMS_CONDITIONS from "scenes/TermsAndConditions/conditions";
import { ReactFCC } from "src/types/types";
import CustomLink from "components/Chakra/CustomLink";
import Checkbox from "components/Chakra/Checkbox";

interface IProps {
  activeTab: "personal" | "bussines";
}

const SubSection: ReactFCC<{ title?: string }> = ({ title, children }) => (
  <Box mt="40px">
    {title && (
      <Text as="h3" fontWeight="bold">
        {title}
      </Text>
    )}
    {children}
  </Box>
);

const DataProcessing: React.FunctionComponent<IProps> = ({ activeTab }) => (
  <>
    <MetaHead
      title="Poučení o zpracování osobních údajů"
      description="Společnost UlovDomov.cz s.r.o., se sídlem: Lazaretní 925/9, 615 00 Brno, IČ: 29300631, zapsaná v obchodním rejstříku vedeném Krajským soudem v Brně, sp. zn. C72444"
      canonicalUrl="https://www.ulovdomov.cz/obchodni-podminky"
    />
    <Container mb="60px">
      <Heading as="h1" mt="90px">
        Poučení o zpracování osobních údajů
      </Heading>
      <Stack mt="18" mb="60px" spacing="20px" direction="row" justify="center">
        <CustomLink to="/zpracovani-osobnich-udaju">
          <Checkbox
            isChecked={activeTab === "personal"}
            label={<Text>Ochrana osobních údajů</Text>}
          />
        </CustomLink>
        <CustomLink to="/zpracovatelska-smlouva">
          <Checkbox
            isChecked={activeTab === "bussines"}
            label={<Text>Zpracovatelská smlouva</Text>}
          />
        </CustomLink>
      </Stack>
      {activeTab === "bussines" && (
        <Box
          px="5%"
          mb="100px"
          borderColor="ud-grey-200"
          borderWidth="1px"
          borderRadius="8px"
          py="10"
        >
          <Text>{TERMS_CONDITIONS.credit[1].text}</Text>
          {TERMS_CONDITIONS.credit[1].laws?.map((law, lawIndex) => (
            <Stack key={lawIndex} pb="20px" pt="30px">
              <Stack>
                <Heading mt="10px" mb="20px" variant="large_2">
                  {lawIndex + 1}. {law.title}
                </Heading>
                <Stack
                  spacing="20px"
                  pl="15px"
                  direction="column"
                  align="flex-start"
                >
                  {law.paragraphs.map((paragraph, paragraphIndex) => (
                    <Stack
                      direction="column"
                      align="flex-start"
                      key={paragraph.text}
                    >
                      <Stack direction="row" mb="10px" justify="space-between">
                        <Text variant="small_2" fontWeight="bold">
                          {paragraphIndex + 1}.
                        </Text>
                        <Text
                          variant="small_2"
                          sx={{
                            a: {
                              color: "ud-primary",
                            },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: paragraph.text,
                          }}
                        />
                      </Stack>
                      {paragraph?.lines?.map((l) => (
                        <Stack
                          direction="column"
                          pl="30px"
                          spacing="10px"
                          key={l}
                        >
                          <Text
                            variant="small_2"
                            sx={{
                              a: {
                                color: "ud-primary",
                              },
                            }}
                            dangerouslySetInnerHTML={{
                              __html: l,
                            }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Box>
      )}
      {activeTab === "personal" && (
        <Box
          px="5%"
          mb="100px"
          borderColor="ud-grey-200"
          borderWidth="1px"
          borderRadius="8px"
          py="10"
        >
          <Heading as="h2" variant="h3">
            Jaké má UlovDomov.cz s.r.o. zásady ochrany osobních údajů
          </Heading>
          <SubSection>
            <Text>
              Vaše údaje pečlivě střežíme. Ať už jste náš klient, kterému
              poskytujeme služby, osoba propojená s klientem, nebo od nás pouze
              získáváte newsletter s tipy a novinkami o našich službách,
              přečtěte si prosíme pečlivě tyto zásady, a pokud máte jakékoliv
              dotazy, <CustomLink to="/kontakt">kontaktujte nás</CustomLink>
              . <br />
              Informace od nás můžete dostávat buď to pod naši značkou UlovDomov
              anebo Ideální Nájemce. Jsme členem skupiny Miton vedle projektů
              jako Slevomat, Rohlík.cz, Bonami či Twisto. Máme pobočky v Brně
              a v Praze a velký tým, který se stará o blaho našich klientů,
              podnájemníků a realitních kanceláří.
            </Text>
          </SubSection>
          <SubSection title="V těchto zásadách ochrany vašeho soukromí vysvětlujeme:">
            <UnorderedList pl={["0", "4"]}>
              <Text as="li">kdo, proč a jak shromažďuje vaše osobní údaje</Text>
              <Text as="li">
                jaké osobní údaje shromažďujeme, zpracováváme nebo uchováváme,
              </Text>
              <Text as="li">jak chráníme vaše osobní údaje a data,</Text>
              <Text as="li">proč a jak používáme cookies,</Text>
              <Text as="li">
                a jakým způsobem se můžete informovat o zpracování vašich
                osobních údajů a získat k těmto údajům přístup a případně vaše
                osobní údaje opravit či aktualizovat.
              </Text>
            </UnorderedList>
          </SubSection>
          <SubSection title="Kdo, proč a jak shromažďuje vaše osobní údaje">
            <Text>
              Vaše osobní údaje shromažďuje a zpracovává za pomocí formulářů
              uvedených na webu ulovdomov.cz a ulovdomov.cz (dále jen Web)
              společnost UlovDomov.cz s.r.o., IČO: 293 00 631, se sídlem:
              Lazaretní 925/9, 615 00 Brno (dále jen UlovDomov) výhradně
              v mezích Nařízení Evropského parlamentu a Rady (EU) 2016/679 (dále
              jen zákonné předpisy).
            </Text>
          </SubSection>
          <SubSection title="Účel zpracování, tedy proč">
            <Text>
              Zpracováváme osobní údaje, které jste nám poskytl/a, a to buď
              z důvodu plnění na základě vaší objednávky (plnění smlouvy) nebo
              s ní související (služby Ideální nájemce, tj nacenění výše
              pronájmu, či zájem o podnájem/Prověření rizikovosti/Zpracování
              vyúčtování), anebo pokud jste si přihlásil/a odběr newsletteru či
              došlo k odsouhlasení marketingových cookies.
            </Text>
            <Table variant="simple" my="30px">
              <Tbody>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">
                      Plnění smlouvy (včetně jejího sjednání)
                    </Text>
                    <Text>
                      Vaše osobní údaje zpracováváme, pokud máte zájem o naše
                      služby a uzavřeme spolu smlouvu. Jedná se o smlouvy
                      produktu Ideální nájemce, tj nacenění výše pronájmu, či
                      zájem o podnájem/Prověření rizikovosti/Zpracování
                      vyúčtování či jiné plnění požadavků klientů dle
                      objednávek; vztahy s obchodními partnery, například
                      partnerskými dodavateli výrobků a služeb. Vyřizování
                      požadavků zaslaných prostřednictvím elektronických
                      formulářů Vyřizování vašich dotazů prostřednictvím Webu.
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">
                      Souhlas se zpracováním osobních údajů
                    </Text>
                    <Text>
                      Pokud nám udělíte souhlas se zpracováním osobních údajů za
                      konkrétním účelem zpracování, budeme Vaše osobní údaje za
                      tímto účelem zpracovávat, dokud tento svůj souhlas
                      neodvoláte. Jedná se o zasílání obchodních sdělení či
                      newsletterů, zejména z oblasti nemovitostí a s tím
                      související problematiky, a nabízení služeb
                      prostřednictvím e-mailu; pro cílení reklamy mohou být
                      využívány také údaje získané při návštěvě Webových
                      stránek. Vyřizování požadavků zaslaných prostřednictvím
                      elektronických formulářů Vyřizování vašich dotazů
                      prostřednictvím Webu.
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">Ochrana oprávněného zájmu</Text>
                    <Text>
                      Ochrana sítě proti poškozujícímu jednání nebo ochrana před
                      zneužitím Webu, oborový monitoring služeb, zasílání
                      informací o navazujících službách správce.
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">
                      Ochrana oprávněného zájmu třetích osob
                    </Text>
                    <Text>
                      Smlouvy produktu Ideální nájemce, tj nacenění výše
                      pronájmu, či zájem o podnájem/Prověření
                      rizikovosti/Zpracování vyúčtování či jiné plnění požadavků
                      klientů dle objednávek.
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">
                      Plnění zákonných povinností
                    </Text>
                    <Text>
                      v případě, kdy Vám poskytneme některé naše služby, může se
                      stát, že následně budeme zpracovávat Vaše údaje z důvodů
                      plnění zákonných povinností (daňové předpisy apod.).
                    </Text>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <Text>
              Při objednávce služeb na Webu, nebo přihlášením odběru
              newsletteru, shromažďujeme vaše osobní údaje nebo jiné údaje
              a nadále je zpracováváme k uvedeným účelům v následujícím rozsahu:
            </Text>
            <Table variant="simple" my="30px">
              <Tbody>
                <Tr>
                  <Td>
                    <Text fontWeight="bold">Údaje subjektů údajů (rozsah)</Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">jméno a&nbsp;příjmení</Text>
                    <Text>
                      Plnění smlouvy, Zasílání obchodních sdělení, nabídka
                      služeb produktů a cílení reklamy, Ochrana oprávněného
                      zájmu správců, Vyřizování požadavků zaslaných
                      prostřednictvím elektronických formulářů. Účelem bude vždy
                      poskytnutí konkrétní služby.
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">e-mailová adresa</Text>
                    <Text>
                      Plnění smlouvy, Zasílání obchodních sdělení, nabídka
                      služeb a produktů a cílení reklamy, Ochrana oprávněného
                      zájmu správců, Vyřizování požadavků zaslaných
                      prostřednictvím elektronických formulářů. Účelem bude vždy
                      poskytnutí konkrétní služby.
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">telefonní číslo</Text>
                    <Text>
                      Plnění smlouvy, Zasílání obchodních sdělení, nabídka
                      služeb a produktů a cílení reklamy, Ochrana oprávněného
                      zájmu správců, Vyřizování požadavků zaslaných
                      prostřednictvím elektronických formulářů. Účelem bude vždy
                      poskytnutí konkrétní služby.
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">
                      jakékoliv další informace týkající se smluvního partnera
                      či třetích osob*
                    </Text>
                    <Text>
                      Plnění smlouvy, Plnění právních povinností, Zasílání
                      obchodních sdělení a nabídka služeb a produktů, Ochrana
                      oprávněného zájmu třetích osob
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Text fontWeight="semibold">číslo bankovní karty</Text>
                    <Text>Plnění smlouvy</Text>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <Text fontStyle="italic">
              *Osobní údaje třetích osob: UlovDomov rovněž ve smyslu účelů
              zpracování osobních údajů, které jsou uvedeny výše, bude
              zpracovávat osobní údaje třetích osob, zejména osobní údaje
              zákazníků klientů či dodavatelů UlovDomov, popř. jiné údaje, které
              UlovDomov obdrží od klienta či dodavatele v souvislosti
              s uzavřením či plněním smlouvy, to výhradně za účelem plnění smluv
              s klienty či dodavateli. Tyto osobní údaje budou vždy zpracovány
              v souladu s platnými právními předpisy na ochranu osobních údajů.
              UlovDomov bude zpracovávat osobní údaje třetích osob po dobu
              trvání smluvního vztahu s klientem či dodavatelem, a dále je bude
              uchovávat nejpozději do 10 let po ukončení smluvního vztahu nebo
              po dobu stanovenou zvláštními právními předpisy, jsou-li takové.
              Po delší dobu budou pak uchovávány, pokud vznikne v odůvodněném
              případě potřeba uchovávat údaje v souvislosti s konkrétním
              případem.
              <br />
              <br />
              Dále mohou být zpracovávány Vaše další kontaktní údaje Vámi
              poskytnuté a uvedené ve smlouvě
            </Text>
            <Text my="5">(výše uvedené dále jen jako „Osobní údaje“)</Text>
            <Text>
              Účelem zpracování Osobních údajů je to, abychom vám mohli
              poskytnout nacenění služby, nezávazné nacenění služby nebo službu
              spočívající v produktu Ideální nájemce, tj nacenění výše pronájmu,
              či zájem o podnájem/Prověření rizikovosti/Zpracování vyúčtování,
              dále ochrana našich zájmů nebo zájmů našich klientů a dodavatelů
              souvisejících s poskytnutou služnou, a abychom vás mohli
              informovat o novinkách a našich službách a tyto služby Vám ve
              vylepšené podobě mohli v budoucnu nabídnout. K jiným účelům Osobní
              údaje nezpracováváme a nikomu třetímu je nepředáváme, mimo našich
              spolupracovníků (vč. smluvních partnerů), kteří se podílejí na
              tom, aby naše služby byly ještě lepší, a kteří mají smluvně
              zajištěnu povinnost mlčenlivosti.
              <br />
              Pokud se dále rozhodnete nevyužívat informací o novinkách
              souvisejících s naší činností a o našich službách, postačí nám
              poslat e-mail na adresu info@ulovdomov.cz s tím, že si nepřejete,
              abychom Osobní údaje pro tento účel zpracovávali, anebo v zaslaném
              e-mailu odhlásit tento odběr.
              <br />
              Osobní údaje zpracováváme za použití manuálních i automatizovaných
              způsobů zpracování. K automatizovanému zpracování osobních údajů
              dochází zejména pro účely plnění smluvního vztahu a nabízení
              služeb. Zároveň informace také informace získané prostřednictvím
              automatizovaných systémů shromažďujeme, uchováváme nebo využíváme
              ke tvorbě statistiky návštěvnosti webu a porovnávání
              s návštěvností jiných internetových stránek.
            </Text>
          </SubSection>
          <SubSection title="Doba uchování osobních údajů">
            <Text>
              Osobní údaje a další informace uchováváme po dobu 10 let (pokud
              zákonné předpisy nepožadují více), případně po dobu, než je
              odvolán souhlas se zpracováním osobních údajů pro účely
              marketingu, nejdéle 10 let, jsou-li osobní údaje zpracovávány na
              základě souhlasu. Vaše práva k Osobním údajům jsou uvedena
              v souhlasu se zpracováním vašich osobních údajů
            </Text>
          </SubSection>
          <SubSection title="Ochrana a&nbsp;bezpečnost Osobních údajů">
            <Text mb="5">
              UlovDomov zavedl příslušná technicko-organizační opatření za
              účelem zabezpečení Osobních údajů a dalších informací, které
              s námi sdílíte. Mezi tyto opatření patří zejména:
            </Text>
            <UnorderedList pl={["0", "4"]}>
              <Text as="li">
                komunikaci s Webem šifrujeme pomocí protokolu SSL;
              </Text>
              <Text as="li">
                procesy shromažďování a zpracování Osobních údajů pečlivě
                kontrolujeme;
              </Text>
              <Text as="li">
                pořizujeme záložní kopie všech informací a informace zálohujeme
                v off-line prostředí;
              </Text>
              <Text as="li">
                pravidelně sledujeme naše IT systémy a vyhodnocujeme získané
                informace;
              </Text>
              <Text as="li">
                naši zaměstnanci a spolupracovníci jsou pravidelně školeni na
                ochranu osobních údajů a jsou vázáni mlčenlivostí;
              </Text>
              <Text as="li">
                všichni naši dodavatelé dodržují minimálně stejnou úroveň
                zabezpečení jako my a jsou vázáni povinností mlčenlivosti;
              </Text>
              <Text as="li">
                používáme firewally a další zabezpečovací technologie.
              </Text>
            </UnorderedList>
          </SubSection>
          <SubSection title="Hlášení a oznamování bezpečnostních incidentů">
            <Text>
              Pakliže by došlo k bezpečnostnímu incidentu představující únik
              osobních údajů či jejich poškození nebo znehodnocení, bezpečnostní
              incident ohlásíme dozorovému úřadu, kterým je Úřad pro ochranu
              osobních údajů, a to nejpozději ve lhůtě 72 hodin ode dne jeho
              zjištění. Zároveň bude každému subjektu údajů, jehož osobních
              údajů se bezpečnostní incident dotkl, oznámeno, že bezpečnostní
              incident nastal. Zároveň zveřejníme oznámení o bezpečnostním
              incidentu na našich webových stránkách.
            </Text>
          </SubSection>
          <SubSection title="Používání 'Cookies' na Webu">
            <Text>
              Tzv. „cookies“ (z anglického koláčky) je malý soubor dat, které
              Web uloží do vašeho počítače, tabletu nebo mobilního zařízení.
              „Cookies“ slouží mimo jiné k rozlišování preferencí uživatelů Webu
              a k tomu, aby si o vás Web pamatoval určité informace při vaší
              další návštěvě. Pro nastavení Cookies anebo více informací o nich
              navštivte toto naše poučení [PŘÍDAT ODKAZ NA NASTAVENÍ COOKIES]
              Pokud si přejete zablokovat i tyto „cookies“ z Webu, můžete
              nastavit váš internetový prohlížeč tak, aby vás před přijetím
              „cookies“ varoval a odmítnout „cookies“ v případě, že váš
              prohlížeč upozorní na jejich přítomnost. Ukládání „cookies“ můžete
              v prohlížeči také zcela vyloučit nebo již nainstalované „cookies“
              můžete vymazat.
            </Text>
          </SubSection>

          <SubSection title="Vaše práva k&nbsp;Osobním údajům">
            <Text>Jako subjekt ochrany Osobních údajů máte právo:</Text>
            <UnorderedList my="5" pl={["0", "4"]}>
              <Text as="li">
                <i>na přístup k&nbsp;Osobním údajům a na informace </i>(tj.
                právo požadovat informace o tom, zda UlovDomov zpracovává vaše
                údaje, a pokud je zpracovává, máte právo k nim získat přístup);
              </Text>
              <Text as="li">
                <i>na opravu nepřesných a doplnění neúplných osobních údajů</i>{" "}
                (pokud si myslíte, že o vás zpracováváme nepřesné či neúplné
                Osobní údaje, můžete požadovat jejich opravu či doplnění);
              </Text>

              <Text as="li">
                <i>na výmaz</i> (pokud o to požádáte, tak vymažeme Osobní údaje
                - jestliže:
                <UnorderedList pl={["1", "8"]}>
                  <Text as="li">
                    již nejsou potřebné pro účely, pro které byly shromážděny
                    nebo jinak zpracovány,
                  </Text>

                  <Text as="li">zpracování je protiprávní,</Text>

                  <Text as="li">
                    vznesete námitky proti zpracování a neexistují žádné
                    převažující oprávněné důvody pro zpracování vašich Osobních
                    údajů, nebo
                  </Text>

                  <Text as="li">
                    odpadla zákonná povinnost ke zpracování stanovená právem
                    Evropské unie nebo vnitrostátními právními předpisy);
                  </Text>
                </UnorderedList>
              </Text>

              <Text as="li">
                <i>na omezení zpracování osobních údajů</i> (pokud o to
                požádáte, znepřístupníme, dočasně odstraníme či provedeme jiné
                úkony zpracování, které budou potřebné pro řádný výkon vámi
                uplatněného práva);
              </Text>

              <Text as="li">
                <i>na přenositelnost údajů</i> (které zpracováváme
                v elektronické formě na základě smlouvy nebo souhlasu, k třetímu
                subjektu, a tomuto vašemu požadavku máme právo nevyhovět, pokud
                by tímto byly nepříznivě dotčena práva a svobody jiných osob);
              </Text>

              <Text as="li">
                <i>vznést námitku</i> (proti zpracování osobních údajů, které
                jsou zpracovávány pro účely splnění úkolu prováděného ve
                veřejném zájmu nebo při výkonu veřejné moci nebo pro účely
                ochrany oprávněných zájmů UlovDomov – kdy pokud neprokážeme
                existenci oprávněného důvodu pro zpracování, zpracování bude na
                základě námitky bezodkladně ukončeno);
              </Text>
            </UnorderedList>
            <Text>
              které můžete kdykoliv uplatnit.
              <br />
              Zároveň máte právo podat stížnost k dozorovému úřadu, kterým je
              Úřad pro ochranu osobních údajů{" "}
              <CustomLink href="https://www.uoou.cz" isExternal>
                https://www.uoou.cz
              </CustomLink>
              , a to v případě, že máte za to, že bylo Vaše právo porušeno anebo
              obecně dochází k porušování práva na ochranu osobních údajů.
            </Text>
            <Text mt="5">
              Veškerá Vaše práva můžete uplatnit u nás, na naši e-mailové adrese
              info@ulovdomov.cz
            </Text>
            <Text mt="5">
              Vezměte prosím na vědomí, že v některých případech, zejména pokud
              si přejete, abychom Osobní údaje smazali či ukončili jejich
              zpracovávání, to může znamenat, že vám již nebudeme moci
              poskytovat některé naše služby. Zároveň vás upozorňujeme na to, že
              pokud budou vaše žádosti podávány opakovaně nebo nedůvodně, mohou
              být z naší strany odmítnuty nebo bychom vám mohli naúčtovat
              poplatek, o jehož výši byste byli dopředu informováni.
            </Text>
            <Text mt="5">
              Osobní údaje zpracovávají pouze naši zaměstnanci, spolupracovníci
              a dodavatelé, kteří se podílejí na tom, aby naše služby byly ještě
              lepší, a kteří mají smluvně zajištěnu povinnost mlčenlivosti.
              Všechny tyto osoby zpracovávají Vaše osobní údaje pouze za výše
              vymezenými účely. Jiným osobám Vaše osobní údaje nepředáváme. Vaše
              osobní údaje nejsou předávány do třetích zemích.
            </Text>
          </SubSection>
          <SubSection title="Dotazy ohledně ochrany Osobních údajů">
            <Text>
              Pokud budete mít jakékoli dotazy, které se týkající těchto
              pravidel, ochrany vašeho soukromí či stížnost na způsob, jakým
              využíváme Osobní údaje, obraťte se prosím na info@ulovdomov.cz.{" "}
              <br />
              Tyto zásady mohou být čas od času změněny. Nabývají platnosti
              11.10.2021.
            </Text>
          </SubSection>
        </Box>
      )}
    </Container>
  </>
);

export const DataProcessingPersonalRoute: React.FC = () => (
  <DataProcessing activeTab="personal" />
);

export const DataProcessingBussinessRoute: React.FC = () => (
  <DataProcessing activeTab="bussines" />
);

DataProcessing.defaultProps = {
  activeTab: "personal",
};
